import { db, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  Timestamp,
  serverTimestamp,
  writeBatch,
  doc
} from 'firebase/firestore';

export interface BackupSummary {
  students: number;
  staff: number;
  classes: number;
  attendanceRecords: number;
  staffAbsences: number;
  transportRecords: number;
  holidays: number;
}

export interface BackupRecord {
  id?: string;
  timestamp: any;
  createdBy: string;
  data?: string; // JSON string of all data (or empty if chunked)
  summary: BackupSummary;
  chunkCount?: number;
  dataLength?: number;
}

const COLLECTIONS_TO_BACKUP = [
  'students',
  'staff',
  'classes',
  'studentAttendance',
  'staffAbsences',
  'transportAttendance',
  'substitutes',
  'absenceReasons',
  'staffRoles',
  'transportLines',
  'holidays',
  'holidayActivities',
  'holidayAttendance',
  'academicYears',
  'classAcademicYears'
];

export const getBackupData = async (backup: BackupRecord): Promise<string> => {
  // If backup already contains the data string (older backups or small backups)
  if (backup.data && backup.data.trim().length > 0) {
    return backup.data;
  }

  if (!backup.id) return '';

  try {
    const chunksCol = collection(db, 'backups', backup.id, 'chunks');
    const q = query(chunksCol, orderBy('index', 'asc'));
    const snap = await getDocs(q);
    
    if (snap.empty) {
      return backup.data || '';
    }

    const orderedChunks = snap.docs.map(d => d.data());
    orderedChunks.sort((a: any, b: any) => (a.index || 0) - (b.index || 0));
    return orderedChunks.map((c: any) => c.text || '').join('');
  } catch (error) {
    console.error("Error loading backup chunks:", error);
    return backup.data || '';
  }
};

export const performBackup = async (userId: string): Promise<BackupRecord | null> => {
  try {
    const allData: { [key: string]: any[] } = {};
    const summary: BackupSummary = {
      students: 0,
      staff: 0,
      classes: 0,
      attendanceRecords: 0,
      staffAbsences: 0,
      transportRecords: 0,
      holidays: 0
    };

    // Fetch all data from all collections
    for (const colName of COLLECTIONS_TO_BACKUP) {
      try {
        const snapshot = await getDocs(collection(db, colName));
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        allData[colName] = docs;

        // Update summary
        if (colName === 'students') summary.students = docs.length;
        if (colName === 'staff') summary.staff = docs.length;
        if (colName === 'classes') summary.classes = docs.length;
        if (colName === 'studentAttendance') summary.attendanceRecords = docs.length;
        if (colName === 'staffAbsences') summary.staffAbsences = docs.length;
        if (colName === 'transportAttendance') summary.transportRecords = docs.length;
        if (colName === 'holidays') summary.holidays = docs.length;
      } catch (error) {
        if (isQuotaError(error)) {
          console.warn(`Backup fetch paused for ${colName}: Quota exceeded`);
          throw error; // Re-throw to be caught by the outer catch
        }
        console.error(`Error fetching ${colName} for backup:`, error);
      }
    }

    const fullJson = JSON.stringify(allData);
    // Firestore has a strict 1,048,576 bytes limit per document.
    // We chunk the JSON into 450 KB pieces stored in a subcollection to guarantee it never exceeds limits.
    const CHUNK_SIZE = 450 * 1024;
    const chunks: string[] = [];
    for (let i = 0; i < fullJson.length; i += CHUNK_SIZE) {
      chunks.push(fullJson.slice(i, i + CHUNK_SIZE));
    }

    const isSmall = fullJson.length < 400 * 1024;

    const backupData: any = {
      timestamp: serverTimestamp(),
      createdBy: userId,
      data: isSmall ? fullJson : '', // Keep main document small to prevent 1MB limit error
      summary,
      chunkCount: chunks.length,
      dataLength: fullJson.length
    };

    const docRef = await addDoc(collection(db, 'backups'), backupData);

    // Save chunks to subcollection
    const chunksCol = collection(db, 'backups', docRef.id, 'chunks');
    const batch = writeBatch(db);
    chunks.forEach((chunkText, idx) => {
      const chunkDoc = doc(chunksCol, String(idx));
      batch.set(chunkDoc, {
        index: idx,
        text: chunkText
      });
    });
    await batch.commit();

    return { id: docRef.id, ...backupData, data: fullJson };
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Backup failed: Quota exceeded");
      return null;
    }
    handleFirestoreError(error, OperationType.WRITE, 'backups');
    console.error("Backup failed:", error);
    return null;
  }
};

export const getLastBackup = async (): Promise<BackupRecord | null> => {
  try {
    const q = query(collection(db, 'backups'), orderBy('timestamp', 'desc'), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as BackupRecord;
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Error fetching last backup: Quota exceeded");
    } else {
      console.error("Error fetching last backup:", error);
    }
    return null;
  }
};

export const getBackupHistory = async (count: number = 10): Promise<BackupRecord[]> => {
  try {
    const q = query(collection(db, 'backups'), orderBy('timestamp', 'desc'), limit(count));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BackupRecord));
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Error fetching backup history: Quota exceeded");
    } else {
      console.error("Error fetching backup history:", error);
    }
    return [];
  }
};

export const shouldTriggerAutoBackup = (lastBackup: BackupRecord | null): boolean => {
  if (!lastBackup) return true;
  
  const lastTimestamp = lastBackup.timestamp instanceof Timestamp 
    ? lastBackup.timestamp.toDate() 
    : new Date(lastBackup.timestamp);
    
  const now = new Date();
  const diffInHours = (now.getTime() - lastTimestamp.getTime()) / (1000 * 60 * 60);
  
  return diffInHours >= 24; // Trigger if older than 24 hours
};

export const resetSystem = async (): Promise<boolean> => {
  try {
    const collectionsToClear = [
      'students',
      'staff',
      'classes',
      'studentAttendance',
      'staffAbsences',
      'transportAttendance',
      'substitutes',
      'absenceReasons',
      'staffRoles',
      'transportLines',
      'holidays',
      'holidayActivities',
      'holidayAttendance',
      'dashboard_summary'
    ];

    for (const colName of collectionsToClear) {
      try {
        const snapshot = await getDocs(collection(db, colName));
        const docs = snapshot.docs;
        
        // Process in batches of 500 (Firestore limit)
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = docs.slice(i, i + 500);
          chunk.forEach((d) => {
            batch.delete(d.ref);
          });
          await batch.commit();
        }
      } catch (error) {
        if (isQuotaError(error)) {
          console.warn(`System reset paused for ${colName}: Quota exceeded`);
          throw error;
        }
        console.error(`Error clearing ${colName}:`, error);
      }
    }
    return true;
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("System reset failed: Quota exceeded");
    } else {
      console.error("System reset failed:", error);
    }
    return false;
  }
};

export const restoreBackup = async (backup: BackupRecord): Promise<boolean> => {
  try {
    const rawData = await getBackupData(backup);
    if (!rawData) throw new Error("לא נמצאו נתוני גיבוי לשחזור");

    // 1. Reset the system first to clear current data
    const resetSuccess = await resetSystem();
    if (!resetSuccess) throw new Error("Failed to clear current data before restore");

    // 2. Parse the backup data
    const data = JSON.parse(rawData);

    // 3. Restore each collection
    for (const colName in data) {
      try {
        const docs = data[colName];
        
        // Process in batches of 500
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = docs.slice(i, i + 500);
          
          chunk.forEach((docData: any) => {
            const { id, ...rest } = docData;
            const docRef = doc(db, colName, id);
            batch.set(docRef, rest);
          });
          
          await batch.commit();
        }
      } catch (error) {
        if (isQuotaError(error)) {
          console.warn(`Restore paused for ${colName}: Quota exceeded`);
          throw error;
        }
        console.error(`Error restoring ${colName}:`, error);
      }
    }
    return true;
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Restore failed: Quota exceeded");
    } else {
      console.error("Restore failed:", error);
    }
    return false;
  }
};

export const getRemoteBackupUrl = async (): Promise<string> => {
  try {
    const { getDoc, doc } = await import('firebase/firestore');
    const docRef = doc(db, 'systemSettings', 'backup');
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data().remoteUrl || '' : '';
  } catch (error) {
    console.error("Error fetching remote backup URL:", error);
    return '';
  }
};

export const updateRemoteBackupUrl = async (url: string): Promise<void> => {
  try {
    const { setDoc, doc } = await import('firebase/firestore');
    const docRef = doc(db, 'systemSettings', 'backup');
    await setDoc(docRef, { remoteUrl: url }, { merge: true });
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, 'systemSettings');
  }
};

export const performRemoteBackup = async (data: any): Promise<boolean> => {
  const remoteUrl = await getRemoteBackupUrl();
  if (!remoteUrl) return false;

  try {
    const response = await fetch(remoteUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        data: data
      })
    });

    return response.ok;
  } catch (error) {
    console.error("Remote backup failed:", error);
    return false;
  }
};
